import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Card, CardContent, CardHeader, Divider, Stack } from '@packages/shared';
import { AvailabilityInfo } from '@packages/modules';
import { useIntl } from 'react-intl';
import { DeliveryInfoCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/DeliveryInfoCardFragmentFragmentDoc';
import { ServiceLayer } from '@packages/cms-components';
import { CarrierDeliveryInfo } from '../CarrierDeliveryInfo';
import { ExpressServiceInfo } from '../ExpressServiceInfo';
import { messages } from './messages';
import { Shortage } from '../Shortage';

/* GraphQL */ `
  fragment DeliveryInfoCardFragment on Product {
    ...AvailabilityInfoFragment
    availability {
      hasExpressService
      ...ShortageFragment
    }
    delivery {
      deliverySize
      ...CarrierDeliveryInfoFragment
    }
  }
`;

type DeliveryProps = {
  data: FragmentType<typeof DeliveryInfoCardFragmentFragmentDoc>;
};

/**
 * This component is a wrapper to show all delivery infos for a given product.
 * Its subcomponents are:
 * <ul>
 *  <li>`AvailabilityInfo`</li>
 *  <li>`CarrierDeliveryInfo`</li>
 *  <li>`ExpressServiceInfo`</li>
 * </ul>
 */
export const DeliveryInfoCard = ({ data }: DeliveryProps) => {
  const product = getFragmentData(DeliveryInfoCardFragmentFragmentDoc, data);

  const {
    delivery: { deliverySize },
    availability,
  } = product;

  const { formatMessage } = useIntl();
  return (
    <Card>
      <CardHeader
        title={formatMessage(messages.deliverycardHeadline)}
        sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
      />
      <CardContent>
        <Stack spacing={1} alignItems="flex-start">
          <Shortage data={availability} />
          <div id="dy_info_verfuegbarkeit_dv" />
          <AvailabilityInfo
            data={product}
            serviceLayer={<ServiceLayer serviceLayerId="eco_delivery_co2neutral" />}
          />
          {/* there is no carrier delivery for normal sized products (size === "DEFAULT"), they are sent by standard delivery */}
          {deliverySize !== 'DEFAULT' && <CarrierDeliveryInfo data={product.delivery} />}
          {availability.hasExpressService && (
            <>
              <Divider />
              <ExpressServiceInfo />
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
